import type { ReactNode } from 'react';
import type {
  CarStatus,
  ChecklistResponseValue,
  FindingClassification,
} from '../../lib/audit-api.js';

/**
 * App 3's vocabulary, rendered once.
 *
 * Every label here appears on more than one screen, and an audit is the one
 * place in QMS360 where a word being slightly different in two places is a real
 * problem: "Minor NC" on the findings screen and "minor non-conformity" in the
 * log are the same thing, and a reader comparing them has to stop and check.
 */

export const CLASSIFICATION_LABEL: Record<FindingClassification, string> = {
  conformity: 'Conformity',
  observation: 'Observation',
  minor_nc: 'Minor NC',
  major_nc: 'Major NC',
};

export const RESPONSE_LABEL: Record<ChecklistResponseValue, string> = {
  conforms: 'Conforms',
  does_not_conform: 'Does not conform',
  not_applicable: 'Not applicable',
  unanswered: 'Unanswered',
};

export const CAR_STATUS_LABEL: Record<CarStatus, string> = {
  issued: 'Issued',
  in_progress: 'In progress',
  submitted: 'Submitted for verification',
  verifying: 'Being verified',
  closed_effective: 'Closed — effective',
  closed_not_effective: 'Closed — NOT effective',
  follow_up_scheduled: 'Follow-up scheduled',
};

/**
 * The tone a status is shown in.
 *
 * `closed_not_effective` is deliberately **not** shown as a success. It is a
 * finished corrective action that did not work, and colouring it like a
 * completed one would tell a reader the opposite of what happened.
 */
type Tone = 'neutral' | 'positive' | 'warning' | 'critical';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-content-secondary',
  positive: 'bg-tone-approved-surface text-content-primary',
  warning: 'bg-tone-info-surface text-content-primary',
  critical: 'bg-tone-critical-surface text-content-primary',
};

export function carTone(status: CarStatus): Tone {
  switch (status) {
    case 'closed_effective':
      return 'positive';
    case 'closed_not_effective':
      return 'critical';
    case 'submitted':
    case 'verifying':
    case 'follow_up_scheduled':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function classificationTone(classification: FindingClassification): Tone {
  switch (classification) {
    case 'conformity':
      return 'positive';
    case 'major_nc':
      return 'critical';
    case 'minor_nc':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * A value that may legitimately be absent.
 *
 * Renders an em dash with a reason rather than a blank, for the reason ADR-0007
 * gives: a blank cell is indistinguishable from a value nobody has entered yet,
 * and in an audit record that difference is the whole point.
 */
export function Absent({ reason }: { reason: string }) {
  return (
    <span className="text-content-secondary" title={reason} aria-label={reason}>
      —
    </span>
  );
}
